import { yupResolver } from '@hookform/resolvers/yup';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { useContext, useEffect, useMemo } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router-dom';

import ToastContext from '../../../../context/ToastContext';
import useAxios from '../../../../hooks/useAxios';
import { WithData } from '../../../../types/api';
import {
  MunicipalityCollection,
  MunicipalityPlaceCollection,
} from '../../../../types/api/municipalities';
import { OrderResource } from '../../../../types/api/orders';
import { PlaceStreetCollection } from '../../../../types/api/places';
import { isStreetFreeInputAllowed } from '../../../../utils/constants/misc';
import { errorToast, successToast } from '../../../../utils/helpers/primereact';
import FieldWithErrors from '../../../Forms/ReactHookForm/FieldWithErrors/FieldWithErrors';
import UnauthPage from '../../../page/UnauthPage';
import {
  getDefaultValues,
  getResolverLabelKey,
  getValidationSchema,
  isResolvedStatus,
} from './IncompleteAddress.functions';

type RouteParams = {
  code: string;
};

const IncompleteAddressFormName = 'incomplete-address-form';

const clientHeaders = {
  'X-Client-Id': process.env.REACT_APP_CLIENT_ID,
};

function IncompleteAddress(): JSX.Element {
  const { t } = useTranslation();
  const { code } = useParams<RouteParams>();
  const history = useHistory();
  const toastRef = useContext(ToastContext)?.toastRef!;

  const { data, isLoading, reload, error } = useAxios<any>(
    { url: `/v2/address/incomplete/code/${code}`, headers: clientHeaders },
    { skipWhen: !code }
  );

  const resolved = isResolvedStatus(data?.status);

  const { data: orderData } = useAxios<OrderResource>(
    {
      url: `/orders/track/${data?.orderSerialNumber}`,
      headers: clientHeaders,
    },
    { skipWhen: !data?.orderSerialNumber }
  );

  const defaultValues = useMemo(() => getDefaultValues(code, data), [code, data]);

  const resolver = useMemo(() => yupResolver(getValidationSchema(t)), [t]);
  const methods = useForm({ defaultValues, resolver });
  const { setValue, watch, handleSubmit, reset } = methods;
  const values = watch();

  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, reset]);

  useEffect(() => {
    if (error) {
      errorToast(
        toastRef,
        t('Address Update Failed'),
        t(
          'An error occurred while trying to edit Address. Please check your inputs.'
        )
      );
    }
  }, [error, t, toastRef]);

  function handleFormSubmission(values: any) {
    const toId = (value: any) =>
      value !== null && value !== undefined && value !== ''
        ? Number(value)
        : null;

    const payload = {
      ...values,
      municipalityId: toId(values.municipalityId),
      placeId: toId(values.placeId),
      streetId: toId(values.streetId),
    };

    reload({
      url: `/v2/address/incomplete/code/${code}`,
      data: payload,
      method: 'PUT',
      headers: clientHeaders,
      successCallback: () => {
        successToast(
          toastRef,
          t('Address Updated'),
          t('Address updated successfully.')
        );
      },
    });
  }

  const { data: municipalitiesData, isLoading: isMunicipalitiesLoading } =
    useAxios<WithData<MunicipalityCollection>>({
      url: '/municipalities?limit=0',
      headers: clientHeaders,
    });

  const {
    data: placesData,
    reload: reloadPlaces,
    isLoading: isPlacesLoading,
  } = useAxios<MunicipalityPlaceCollection>(
    {
      url: `/municipalities/${values?.municipalityId}/places?limit=0`,
      headers: clientHeaders,
    },
    { skipWhen: !values?.municipalityId }
  );

  const {
    data: streetsData,
    reload: reloadStreets,
    isLoading: isStreetsLoading,
  } = useAxios<WithData<PlaceStreetCollection>>(
    {
      url: `/places/${values?.placeId}/streets?limit=0`,
      headers: clientHeaders,
    },
    { skipWhen: !values?.placeId }
  );

  const municipalityOptions = useMemo(
    () =>
      municipalitiesData?.data?.map((municipality) => ({
        value: String(municipality.id),
        label: municipality.name,
      })) ?? [],
    [municipalitiesData]
  );

  const placeOptions = useMemo(
    () =>
      placesData?.map((place) => ({
        value: String(place.place_id),
        label: place.place_name,
        postal_code: place.postenski_broj,
      })) ?? [],
    [placesData]
  );

  const streetOptions = useMemo(
    () =>
      streetsData?.data?.map((street) => ({
        value: String(street.id),
        label: street.name,
      })) ?? [],
    [streetsData]
  );

  const displayAddress = resolved ? data?.resolvedAddress : data?.address;
  const streetSummary =
    displayAddress?.streetName || displayAddress?.rawName || '-';

  return (
    <UnauthPage title={t('Update delivery address')}>
      <p>{t('Order details')}</p>
      <ul style={{ listStyleType: 'none', paddingLeft: 0 }}>
        <li className="p-mb-2">
          <b>{t('Serial Number')}</b>: {data?.orderSerialNumber ?? '-'}
        </li>
        <li className="p-mb-2">
          <b>{t('Sender')}</b>: {orderData?.klient_od_ime ?? '-'}
        </li>
        <li className="p-mb-2">
          <b>{t('Recipient')}</b>: {orderData?.klient_do_ime ?? '-'}
        </li>
        <li className="p-mb-2">
          <b>{t('Delivery address')}</b>:
          <ul style={{ listStyleType: "'- '", marginTop: '0.5rem' }}>
            <li className="p-mb-2">
              <b>{t('Municipality')}</b>:{' '}
              {displayAddress?.municipalityName ?? '-'}
            </li>
            <li className="p-mb-2">
              <b>{t('Place')}</b>: {displayAddress?.placeName ?? '-'}
            </li>
            <li className="p-mb-2">
              <b>{t('Street')}</b>: {streetSummary}
            </li>
          </ul>
        </li>
      </ul>

      {resolved ? (
        <>
          <p>
            {t('The address has already been corrected by {{resolver}}', {
              resolver: t(getResolverLabelKey(data?.status)),
            })}
          </p>
          <Button
            type="button"
            label={t('OK')}
            className="p-mt-5"
            onClick={() => history.push('/login')}
          />
        </>
      ) : (
        <>
          <p>
            {t('Please confirm the address and fill in the missing fields')}
          </p>
          <FormProvider {...methods}>
            <form
              id={IncompleteAddressFormName}
              onSubmit={handleSubmit(handleFormSubmission)}
            >
              <div className="p-fluid">
                <FieldWithErrors name="municipalityId" label={t('Municipality')}>
                  <Controller
                    name="municipalityId"
                    render={({ field }) => (
                      <Dropdown
                        {...field}
                        id="municipalityId"
                        name="municipalityId"
                        options={municipalityOptions}
                        value={field.value}
                        onChange={(e) => {
                          if (e.value) {
                            field.onChange(e.value);
                            const item = municipalityOptions?.find(
                              (s) => s.value === e.value
                            );
                            setValue('municipalityName', item?.label ?? '');
                            setValue('placeId', null);
                            setValue('placeName', '');
                            setValue('streetId', null);
                            setValue('streetName', '');
                            setValue('housenumber', '');
                            setValue('entrance', '');
                            setValue('apartment', '');

                            reloadPlaces({
                              url: `municipalities/${e.value}/places?limit=0`,
                            });
                          } else {
                            setValue('municipalityId', null);
                            setValue('municipalityName', '');
                            setValue('placeId', null);
                            setValue('placeName', '');
                            setValue('streetId', null);
                            setValue('streetName', '');
                            setValue('housenumber', '');
                            setValue('entrance', '');
                            setValue('apartment', '');
                          }
                        }}
                        filter
                        filterPlaceholder={t('Search')}
                        placeholder={
                          isMunicipalitiesLoading ? t('Loading...') : ''
                        }
                        disabled={isMunicipalitiesLoading}
                        className="data-cy-municipalityId"
                        optionLabel="label"
                        optionValue="value"
                      />
                    )}
                  />
                </FieldWithErrors>

                <FieldWithErrors name="placeId" label={t('Place')}>
                  <Controller
                    name="placeId"
                    render={({ field }) => (
                      <Dropdown
                        {...field}
                        id="placeId"
                        name="placeId"
                        options={placeOptions}
                        value={field.value}
                        onChange={(e) => {
                          if (e.value) {
                            field.onChange(e.value);
                            const item = placeOptions?.find(
                              (s) => s.value === e.value
                            );
                            setValue('placeName', item?.label ?? '');

                            reloadStreets({
                              url: `places/${e.value}/streets?limit=0`,
                            });
                          } else {
                            setValue('placeId', null);
                            setValue('placeName', '');
                            setValue('streetId', null);
                            setValue('streetName', '');
                            setValue('housenumber', '');
                            setValue('entrance', '');
                            setValue('apartment', '');
                          }
                        }}
                        filter
                        filterPlaceholder={t('Search')}
                        placeholder={isPlacesLoading ? t('Loading...') : ''}
                        disabled={!values?.municipalityId || isPlacesLoading}
                        className="data-cy-placeId"
                        optionLabel="label"
                        optionValue="value"
                      />
                    )}
                  />
                </FieldWithErrors>

                {isStreetFreeInputAllowed ? (
                  <FieldWithErrors name="rawName" label={t('Address')}>
                    <Controller
                      name="rawName"
                      render={({ field }) => (
                        <InputText
                          id="rawName"
                          name="rawName"
                          value={field.value}
                          onChange={field.onChange}
                          disabled={!values?.placeName || isStreetsLoading}
                        />
                      )}
                    />
                  </FieldWithErrors>
                ) : (
                  <>
                    <FieldWithErrors name="streetId" label={t('Street')}>
                      <Controller
                        name="streetId"
                        render={({ field }) => (
                          <Dropdown
                            id="streetId"
                            name="streetId"
                            options={streetOptions}
                            value={field.value}
                            onChange={(e) => {
                              if (e.value) {
                                field.onChange(e.value);
                                const item = streetOptions?.find(
                                  (s) => s.value === e.value
                                );
                                setValue('streetName', item?.label ?? '');
                              } else {
                                setValue('streetId', null);
                                setValue('streetName', '');
                                setValue('housenumber', '');
                                setValue('entrance', '');
                                setValue('apartment', '');
                              }
                            }}
                            filter
                            filterPlaceholder={t('Search')}
                            placeholder={
                              isStreetsLoading ? t('Loading...') : ''
                            }
                            disabled={!values?.placeId || isStreetsLoading}
                            className="data-cy-streetId"
                          />
                        )}
                      />
                    </FieldWithErrors>

                    <div className="p-grid">
                      <FieldWithErrors
                        name="housenumber"
                        label={false}
                        className="p-col-4"
                      >
                        <Controller
                          name="housenumber"
                          render={({ field }) => (
                            <InputText
                              id="housenumber"
                              name="housenumber"
                              value={field.value}
                              onChange={field.onChange}
                              disabled={!values?.streetId}
                              placeholder={t('Street No.')}
                            />
                          )}
                        />
                      </FieldWithErrors>

                      <FieldWithErrors
                        name="entrance"
                        label={false}
                        className="p-col-4"
                      >
                        <Controller
                          name="entrance"
                          render={({ field }) => (
                            <InputText
                              id="entrance"
                              name="entrance"
                              value={field.value}
                              onChange={field.onChange}
                              disabled={!values?.streetId}
                              placeholder={t('Entrance No.')}
                            />
                          )}
                        />
                      </FieldWithErrors>

                      <FieldWithErrors
                        name="apartment"
                        label={false}
                        className="p-col-4"
                      >
                        <Controller
                          name="apartment"
                          render={({ field }) => (
                            <InputText
                              id="apartment"
                              name="apartment"
                              value={field.value}
                              onChange={field.onChange}
                              disabled={!values?.streetId}
                              placeholder={t('Flat No.')}
                            />
                          )}
                        />
                      </FieldWithErrors>
                    </div>
                  </>
                )}

                <Button
                  type="submit"
                  label={t('Confirm')}
                  className="p-mt-5"
                  disabled={isLoading}
                />
              </div>
            </form>
          </FormProvider>
        </>
      )}
    </UnauthPage>
  );
}

export default IncompleteAddress;
